export type ConsentState = "granted" | "denied" | "unknown";
export type RewardedAdAvailability =
  | "available"
  | "consent_required"
  | "disabled_for_premium"
  | "not_configured"
  | "unsupported";
export type RewardedAdResult = "reward_granted" | "closed" | "unavailable";
export type RewardedAdUnavailableReason =
  | Exclude<RewardedAdAvailability, "available">
  | "gpt_load_failed"
  | "load_timeout"
  | "no_fill"
  | "slot_unsupported";

export type RewardedProviderUnavailableReason = Extract<
  RewardedAdUnavailableReason,
  "gpt_load_failed" | "load_timeout" | "no_fill" | "slot_unsupported"
>;

export function isRewardedProviderUnavailable(
  reason: RewardedAdUnavailableReason,
): reason is RewardedProviderUnavailableReason {
  return reason === "no_fill"
    || reason === "gpt_load_failed"
    || reason === "load_timeout"
    || reason === "slot_unsupported";
}

export interface RewardedAdPayload {
  amount?: number;
  type?: string;
}

export type RewardedAdOutcome =
  | {
    result: "reward_granted";
    payload: RewardedAdPayload | null;
    reason: null;
  }
  | {
    result: "closed";
    payload: null;
    reason: "closed_without_reward";
  }
  | {
    result: "unavailable";
    payload: null;
    reason: RewardedAdUnavailableReason;
  };

export interface RewardedAdContext {
  adUnitPath?: string;
  consent: ConsentState;
  isPremium: boolean;
}

interface GoogleTagEvent {
  isEmpty?: boolean;
  slot: GoogleTagSlot;
  makeRewardedVisible?: () => void;
  payload?: RewardedAdPayload;
}

interface GoogleTagSlot {
  addService(service: unknown): GoogleTagSlot;
}

interface GoogleTagPubAdsService {
  addEventListener(name: string, listener: (event: GoogleTagEvent) => void): void;
  setPrivacySettings(settings: {
    limitedAds?: boolean;
    nonPersonalizedAds: boolean;
    tagForAgeTreatment: unknown;
  }): void;
}

interface GoogleTagApi {
  cmd: Array<() => void>;
  defineOutOfPageSlot(path: string, format: unknown): GoogleTagSlot | null;
  destroySlots(slots: GoogleTagSlot[]): boolean;
  display(slot: GoogleTagSlot): void;
  enableServices(): void;
  enums: {
    OutOfPageFormat: { INTERSTITIAL?: unknown; REWARDED: unknown };
    TagForAgeTreatment: { UNSPECIFIED: unknown };
  };
  pubads(): GoogleTagPubAdsService;
}

export function applyGeneralAudienceWebAdSettings(
  pubads: Pick<GoogleTagPubAdsService, "setPrivacySettings">,
  unspecifiedAgeTreatment: unknown,
  limitedAds = false,
): void {
  pubads.setPrivacySettings({
    ...(limitedAds ? { limitedAds: true } : {}),
    nonPersonalizedAds: true,
    tagForAgeTreatment: unspecifiedAgeTreatment,
  });
}

type GooglePublisherTagMode = "standard" | "limited";

interface LoadedGooglePublisherTag {
  api: GoogleTagApi;
  mode: GooglePublisherTagMode;
}

function getGooglePublisherTagMode(consent: ConsentState): GooglePublisherTagMode | null {
  if (consent === "unknown") return null;
  return consent === "denied" ? "limited" : "standard";
}

export async function showInterstitialAd(context: RewardedAdContext): Promise<boolean> {
  if (getRewardedAdAvailability(context) !== "available") {
    return false;
  }
  try {
    const loadedTag = await loadGooglePublisherTag(context.consent);
    const googletag = loadedTag.api;
    return await new Promise<boolean>((resolve) => {
      googletag.cmd.push(() => {
        const format = googletag.enums.OutOfPageFormat.INTERSTITIAL;
        if (!format) {
          resolve(false);
          return;
        }
        const slot = googletag.defineOutOfPageSlot(context.adUnitPath as string, format);
        if (!slot) {
          resolve(false);
          return;
        }
        let settled = false;
        const finish = (shown: boolean) => {
          if (settled) return;
          settled = true;
          if (!shown) googletag.destroySlots([slot]);
          resolve(shown);
        };
        const pubads = googletag.pubads();
        applyGeneralAudienceWebAdSettings(
          pubads,
          googletag.enums.TagForAgeTreatment.UNSPECIFIED,
          loadedTag.mode === "limited",
        );
        pubads.addEventListener("slotRenderEnded", (event) => {
          if (event.slot === slot) finish(event.isEmpty !== true);
        });
        slot.addService(pubads);
        googletag.enableServices();
        googletag.display(slot);
        window.setTimeout(() => finish(false), 5000);
      });
    });
  } catch {
    return false;
  }
}

declare global {
  interface Window {
    googletag?: GoogleTagApi;
  }
}

const GPT_SCRIPT_ID = "wordinarius-google-publisher-tag";
const GPT_SCRIPT_URLS: Record<GooglePublisherTagMode, string> = {
  standard: "https://securepubads.g.doubleclick.net/tag/js/gpt.js",
  limited: "https://pagead2.googlesyndication.com/tag/js/gpt.js",
};
const GPT_SCRIPT_LOAD_TIMEOUT_MS = 3_000;
const REWARDED_LOAD_TIMEOUT_MS = 4_000;
const PROVIDER_UNAVAILABLE_CACHE_MS = 15_000;

let gptLoadPromise: Promise<LoadedGooglePublisherTag> | null = null;
let gptLoadMode: GooglePublisherTagMode | null = null;
let recentProviderUnavailable: {
  reason: RewardedProviderUnavailableReason;
  untilMs: number;
} | null = null;

function rememberProviderUnavailable(reason: RewardedProviderUnavailableReason): void {
  recentProviderUnavailable = {
    reason,
    untilMs: Date.now() + PROVIDER_UNAVAILABLE_CACHE_MS,
  };
}

function readRecentProviderUnavailable(): RewardedProviderUnavailableReason | null {
  if (!recentProviderUnavailable || recentProviderUnavailable.untilMs <= Date.now()) {
    recentProviderUnavailable = null;
    return null;
  }
  return recentProviderUnavailable.reason;
}

function clearRecentProviderUnavailable(): void {
  recentProviderUnavailable = null;
}

export function getRewardedAdAvailability(context: RewardedAdContext): RewardedAdAvailability {
  if (context.isPremium) {
    return "disabled_for_premium";
  }
  if (context.consent === "unknown") {
    return "consent_required";
  }
  if (!context.adUnitPath?.trim()) {
    return "not_configured";
  }
  if (typeof window === "undefined" || typeof document === "undefined") {
    return "unsupported";
  }
  return "available";
}

function canUseLoadedTagMode(
  loadedMode: GooglePublisherTagMode,
  requestedMode: GooglePublisherTagMode,
): boolean {
  return loadedMode === requestedMode || loadedMode === "limited";
}

async function loadGooglePublisherTag(consent: ConsentState): Promise<LoadedGooglePublisherTag> {
  const requestedMode = getGooglePublisherTagMode(consent);
  if (!requestedMode) throw new Error("gpt_consent_unresolved");
  window.googletag = window.googletag || ({ cmd: [] } as unknown as GoogleTagApi);
  const existing = document.getElementById(GPT_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing?.dataset.loaded === "true") {
    const loadedMode = existing.dataset.mode === "limited" ? "limited" : "standard";
    if (!canUseLoadedTagMode(loadedMode, requestedMode)) {
      throw new Error("gpt_privacy_mode_conflict");
    }
    return { api: window.googletag, mode: loadedMode };
  }
  const cachedFailure = readRecentProviderUnavailable();
  if (cachedFailure === "gpt_load_failed") {
    throw new Error("gpt_script_load_failed_cached");
  }
  if (gptLoadPromise) {
    if (gptLoadMode === "standard" && requestedMode === "limited") {
      throw new Error("gpt_privacy_mode_conflict");
    }
    return gptLoadPromise;
  }

  gptLoadMode = requestedMode;
  gptLoadPromise = new Promise<LoadedGooglePublisherTag>((resolve, reject) => {
    const script = existing || document.createElement("script");
    let settled = false;
    let loadTimeout: number | null = null;
    const cleanup = () => {
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
      if (loadTimeout !== null) window.clearTimeout(loadTimeout);
    };
    const onLoad = () => {
      if (settled) return;
      settled = true;
      cleanup();
      script.dataset.loaded = "true";
      script.dataset.mode = requestedMode;
      delete script.dataset.failed;
      clearRecentProviderUnavailable();
      resolve({ api: window.googletag as GoogleTagApi, mode: requestedMode });
    };
    const onError = () => {
      if (settled) return;
      settled = true;
      cleanup();
      script.dataset.failed = "true";
      script.remove();
      rememberProviderUnavailable("gpt_load_failed");
      reject(new Error("gpt_script_load_failed"));
    };
    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);
    if (!existing) {
      script.id = GPT_SCRIPT_ID;
      script.async = true;
      script.dataset.mode = requestedMode;
      script.src = GPT_SCRIPT_URLS[requestedMode];
      document.head.appendChild(script);
    }
    loadTimeout = window.setTimeout(onError, GPT_SCRIPT_LOAD_TIMEOUT_MS);
  });
  try {
    return await gptLoadPromise;
  } finally {
    gptLoadPromise = null;
    gptLoadMode = null;
  }
}

export async function preloadRewardedAdProvider(context: RewardedAdContext): Promise<boolean> {
  if (getRewardedAdAvailability(context) !== "available") return false;
  try {
    await loadGooglePublisherTag(context.consent);
    return true;
  } catch {
    return false;
  }
}

export function __resetRewardedAdProviderStateForTests(): void {
  gptLoadPromise = null;
  gptLoadMode = null;
  recentProviderUnavailable = null;
}

export async function showRewardedAd(context: RewardedAdContext): Promise<RewardedAdOutcome> {
  const availability = getRewardedAdAvailability(context);
  if (availability !== "available") {
    return { result: "unavailable", payload: null, reason: availability };
  }
  const cachedUnavailable = readRecentProviderUnavailable();
  if (cachedUnavailable) {
    return { result: "unavailable", payload: null, reason: cachedUnavailable };
  }

  try {
    const loadedTag = await loadGooglePublisherTag(context.consent);
    const googletag = loadedTag.api;
    return await new Promise<RewardedAdOutcome>((resolve) => {
      googletag.cmd.push(() => {
        const slot = googletag.defineOutOfPageSlot(
          context.adUnitPath as string,
          googletag.enums.OutOfPageFormat.REWARDED,
        );
        if (!slot) {
          resolve({ result: "unavailable", payload: null, reason: "slot_unsupported" });
          return;
        }

        let granted = false;
        let rewardPayload: RewardedAdPayload | null = null;
        let settled = false;
        let loadTimeout: number | null = null;
        const finish = (
          result: RewardedAdResult,
          reason: RewardedAdUnavailableReason | "closed_without_reward" | null,
        ) => {
          if (settled) {
            return;
          }
          settled = true;
          if (loadTimeout !== null) {
            window.clearTimeout(loadTimeout);
          }
          googletag.destroySlots([slot]);
          if (result === "reward_granted") {
            resolve({ result, payload: rewardPayload, reason: null });
          } else if (result === "closed") {
            resolve({ result, payload: null, reason: "closed_without_reward" });
          } else {
            if (isRewardedProviderUnavailable(reason as RewardedAdUnavailableReason)) {
              rememberProviderUnavailable(reason as RewardedProviderUnavailableReason);
            }
            resolve({ result, payload: null, reason: reason as RewardedAdUnavailableReason });
          }
        };

        const pubads = googletag.pubads();
        applyGeneralAudienceWebAdSettings(
          pubads,
          googletag.enums.TagForAgeTreatment.UNSPECIFIED,
          loadedTag.mode === "limited",
        );
        pubads.addEventListener("slotRenderEnded", (event) => {
          if (event.slot === slot && event.isEmpty === true) {
            finish("unavailable", "no_fill");
          }
        });
        pubads.addEventListener("rewardedSlotReady", (event) => {
          if (event.slot === slot) {
            clearRecentProviderUnavailable();
            if (loadTimeout !== null) {
              window.clearTimeout(loadTimeout);
              loadTimeout = null;
            }
            event.makeRewardedVisible?.();
          }
        });
        pubads.addEventListener("rewardedSlotGranted", (event) => {
          if (event.slot === slot) {
            granted = true;
            rewardPayload = event.payload || null;
          }
        });
        pubads.addEventListener("rewardedSlotClosed", (event) => {
          if (event.slot === slot) {
            finish(granted ? "reward_granted" : "closed", granted ? null : "closed_without_reward");
          }
        });

        slot.addService(pubads);
        googletag.enableServices();
        loadTimeout = window.setTimeout(
          () => finish("unavailable", "load_timeout"),
          REWARDED_LOAD_TIMEOUT_MS,
        );
        googletag.display(slot);
      });
    });
  } catch {
    return { result: "unavailable", payload: null, reason: "gpt_load_failed" };
  }
}
